import { CampingSpot } from '../types';
import { reverseGeocodeRegion } from './regionEnrichmentService';
import { fetchPropertyInfo } from './matrikkelService';
import { fetchElevation } from './elevationService';

const CONTENT_PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';
const NIBIO_WFS_URL = 'https://wfs.nibio.no/cgi-bin/ar50_2';
const MILJODIREKTORATET_FRILUFT_URL = 'https://kart.miljodirektoratet.no/arcgis/rest/services/friluftsliv_statlig_sikra/MapServer/0/query';
const DEFAULT_STATSKOG_QUERY_URL = 'https://kartportal.statskog.no/server/rest/services/Delt_offentlig/Eiendommer_nedlasting/FeatureServer/0/query';
const STATSKOG_QUERY_URL =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_STATSKOG_ARCGIS_QUERY_URL) ||
  DEFAULT_STATSKOG_QUERY_URL;
const STATSKOG_FALLBACK_URLS = [
  'https://kartportal.statskog.no/server/rest/services/Delt_offentlig/Statskogs_eiendommer/MapServer/0/query',
  'https://ags.statskog.no/arcgis/rest/services/StatensKartverk/StatskogEiendom_SK/MapServer/0/query'
];

const INNMARK_CODES = new Set(['21', '22', '23']);
const innmarkCache = new Map<string, boolean>();
const tagsCache = new Map<string, string[]>();
const slopeCache = new Map<string, number | null>();

const cacheKey = (lat: number, lng: number) => `${lat.toFixed(5)},${lng.toFixed(5)}`;

const fetchTextWithProxyFallback = async (url: string): Promise<string> => {
  const prefersProxy =
    /wfs\.nibio\.no/i.test(url) ||
    /kart\.miljodirektoratet\.no/i.test(url) ||
    /kartportal\.statskog\.no/i.test(url) ||
    /ags\.statskog\.no/i.test(url);

  const tryDirect = async (): Promise<string | null> => {
    try {
      const direct = await fetch(url, { headers: { Accept: '*/*' }, signal: AbortSignal.timeout(8000) });
      if (direct.ok) return await direct.text();
    } catch (err) {
      console.warn('[geoValidation] direct fetch failed', err);
    }
    return null;
  };

  const tryProxy = async (): Promise<string | null> => {
    try {
      const proxyUrl = `${CONTENT_PROXY_URL}?url=${encodeURIComponent(url)}`;
      const proxied = await fetch(proxyUrl, { headers: { Accept: '*/*' }, signal: AbortSignal.timeout(8000) });
      if (proxied.ok) return await proxied.text();
    } catch (err) {
      console.warn('[geoValidation] proxy fetch failed', err);
    }
    return null;
  };

  if (prefersProxy) {
    const proxyText = await tryProxy();
    if (proxyText !== null) return proxyText;
    const directText = await tryDirect();
    if (directText !== null) return directText;
  } else {
    const directText = await tryDirect();
    if (directText !== null) return directText;
    const proxyText = await tryProxy();
    if (proxyText !== null) return proxyText;
  }

  throw new Error('Both direct and proxy fetch failed');
};

const removeVerificationMarker = (features: string[] = []) =>
  features.filter((f) => String(f).toUpperCase() !== 'VERIFISERES');

export const stripVerificationMarkerFromSpot = (spot: CampingSpot): CampingSpot => ({
  ...spot,
  features: removeVerificationMarker(spot.features || [])
});

export const addVerificationMarkerToSpot = (spot: CampingSpot): CampingSpot => ({
  ...spot,
  features: Array.from(new Set([...(spot.features || []), 'VERIFISERES']))
});

const _deprecated_fetchTextWithProxyFallback = async (url: string): Promise<string> => {
  try {
    const direct = await fetch(url, { headers: { Accept: '*/*' } });
    if (direct.ok) return await direct.text();
  } catch (err) {
    console.warn('[geoValidation] direct fetch failed, trying proxy', err);
  }

  const proxyUrl = `${CONTENT_PROXY_URL}?url=${encodeURIComponent(url)}`;
  const proxied = await fetch(proxyUrl, { headers: { Accept: '*/*' } });
  if (!proxied.ok) throw new Error(`Proxy fetch failed (${proxied.status})`);
  return await proxied.text();
};

const extractTagValue = (xml: string, tagName: string): string | null => {
  const safeTag = tagName.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  const regex = new RegExp(`<[^>]*:${safeTag}>([^<]+)</[^>]*:${safeTag}>`, 'i');
  const m = xml.match(regex);
  return m?.[1]?.trim() || null;
};

const queryArcgisByPoint = async (baseUrl: string, lat: number, lng: number): Promise<any[]> => {
  if (!baseUrl) return [];
  const params = new URLSearchParams({
    f: 'json',
    geometry: `${lng},${lat}`,
    geometryType: 'esriGeometryPoint',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: '*',
    returnGeometry: 'false'
  });
  const url = `${baseUrl}?${params.toString()}`;
  const raw = await fetchTextWithProxyFallback(url);
  const parsed = JSON.parse(raw);
  return Array.isArray(parsed?.features) ? parsed.features : [];
};

const getAttributeCaseInsensitive = (attributes: Record<string, any>, key: string): any => {
  const wanted = key.toLowerCase();
  const match = Object.keys(attributes || {}).find((k) => k.toLowerCase() === wanted);
  return match ? attributes[match] : undefined;
};

const isStatskogFeature = (feature: any): boolean => {
  const attrs = feature?.attributes || {};
  const rawCategory =
    getAttributeCaseInsensitive(attrs, 'Eiendomskategori') ??
    getAttributeCaseInsensitive(attrs, 'eiendomskategori') ??
    getAttributeCaseInsensitive(attrs, 'eierkategori') ??
    '';
  const category = String(rawCategory || '').toLowerCase();
  return category.includes('statskog') || category.includes('statsallmenning');
};

export const isInnmark = async (lat: number, lng: number): Promise<boolean> => {
  const key = cacheKey(lat, lng);
  if (innmarkCache.has(key)) return innmarkCache.get(key)!;

  try {
    const params = new URLSearchParams({
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeName: 'ms:AR50',
      srsName: 'EPSG:4326',
      count: '1',
      cql_filter: `INTERSECTS(msGeometry,POINT(${lng} ${lat}))`
    });

    const xml = await fetchTextWithProxyFallback(`${NIBIO_WFS_URL}?${params.toString()}`);
    const arealtype = extractTagValue(xml, 'arealtype');
    const jordbruk = extractTagValue(xml, 'jordbruk');
    const innmark = INNMARK_CODES.has(String(arealtype || '').trim()) || INNMARK_CODES.has(String(jordbruk || '').trim());
    innmarkCache.set(key, innmark);
    return innmark;
  } catch (err) {
    console.warn('[geoValidation] isInnmark failed, defaulting to false', err);
    innmarkCache.set(key, false);
    return false;
  }
};

export const getLocationTags = async (lat: number, lng: number): Promise<string[]> => {
  const key = cacheKey(lat, lng);
  if (tagsCache.has(key)) return tagsCache.get(key)!;

  const tags = new Set<string>();
  try {
    const friluftFeatures = await queryArcgisByPoint(MILJODIREKTORATET_FRILUFT_URL, lat, lng);
    if (friluftFeatures.length > 0) tags.add('Statlig Friluftsområde');
  } catch (err) {
    console.warn('[geoValidation] Miljodirektoratet tag query failed', err);
  }

  try {
    const sources = [STATSKOG_QUERY_URL, ...STATSKOG_FALLBACK_URLS].filter(Boolean);
    for (const source of sources) {
      try {
        const statskogFeatures = await queryArcgisByPoint(source, lat, lng);
        if (statskogFeatures.some(isStatskogFeature) || statskogFeatures.length > 0) {
          tags.add('Statskog');
          break;
        }
      } catch (innerErr) {
        console.warn('[geoValidation] Statskog source failed', source, innerErr);
      }
    }
  } catch (err) {
    console.warn('[geoValidation] Statskog tag query failed', err);
  }

  const result = Array.from(tags);
  tagsCache.set(key, result);
  return result;
};

export const estimateTerrainSlopePercent = async (
  lat: number,
  lng: number,
  sampleDistanceMeters: number = 8
): Promise<number | null> => {
  const key = cacheKey(lat, lng);
  if (slopeCache.has(key)) return slopeCache.get(key)!;

  try {
    const metersToLat = 1 / 111320;
    const cosLat = Math.cos((lat * Math.PI) / 180);
    const metersToLon = 1 / (111320 * Math.max(0.2, Math.abs(cosLat)));
    const dLat = sampleDistanceMeters * metersToLat;
    const dLon = sampleDistanceMeters * metersToLon;

    const [center, north, south, east, west] = await Promise.all([
      fetchElevation({ latitude: lat, longitude: lng }),
      fetchElevation({ latitude: lat + dLat, longitude: lng }),
      fetchElevation({ latitude: lat - dLat, longitude: lng }),
      fetchElevation({ latitude: lat, longitude: lng + dLon }),
      fetchElevation({ latitude: lat, longitude: lng - dLon })
    ]);

    if (typeof center !== 'number') {
      slopeCache.set(key, null);
      return null;
    }

    const neighbors = [north, south, east, west].filter((v): v is number => typeof v === 'number');
    if (neighbors.length < 2) {
      slopeCache.set(key, null);
      return null;
    }

    const maxDiff = neighbors.reduce((acc, h) => Math.max(acc, Math.abs(h - center)), 0);
    const slopePercent = (maxDiff / Math.max(1, sampleDistanceMeters)) * 100;
    const rounded = Math.round(slopePercent * 10) / 10;
    slopeCache.set(key, rounded);
    return rounded;
  } catch {
    slopeCache.set(key, null);
    return null;
  }
};

export const enrichSpotWithGeoData = async (spot: CampingSpot): Promise<CampingSpot> => {
  try {
    // Amenity-/coastal-punkter trenger ikke innmark-sjekk eller eiendomsoppslag
    const isNonRoadAmenity = spot.spotType === 'osm_amenity' || spot.spotType === 'coastal';
    // Hent eierform via Matrikkel bare hvis den ikke allerede er satt
    const needsPropertyFetch = !isNonRoadAmenity && (!spot.property?.eierform || spot.property.eierform === 'Ukjent');

    const shouldCheckTerrainSlope = spot.spotType === 'algorithmic' || spot.spotType === 'confirmed';

    const [innmark, tags, region, fetchedProperty, terrainSlopePercent] = await Promise.all([
      !isNonRoadAmenity && spot.isInnmark === undefined ? isInnmark(spot.location.latitude, spot.location.longitude) : Promise.resolve(spot.isInnmark ?? false),
      getLocationTags(spot.location.latitude, spot.location.longitude),
      reverseGeocodeRegion(spot.location),
      needsPropertyFetch ? fetchPropertyInfo(spot.location) : Promise.resolve(null),
      shouldCheckTerrainSlope
        ? estimateTerrainSlopePercent(spot.location.latitude, spot.location.longitude, 8)
        : Promise.resolve(spot.terrainSlopePercent ?? null),
    ]);

    // Avled eierform fra tags (Statskog og Statlig Friluftsomr.) om Matrikkel ikke ga svar
    const allTags = Array.from(new Set([...(spot.tags || []), ...tags]));
    const derivedEierform = fetchedProperty?.eierform && fetchedProperty.eierform !== 'Ukjent'
      ? fetchedProperty.eierform
      : allTags.includes('Statskog')
        ? 'Statskog' as const
        : allTags.includes('Statlig Friluftsområde')
          ? 'Offentlig' as const
          : (spot.property?.eierform ?? undefined);

    const nextProperty = {
      ...(spot.property || { kommunenavn: '', kommunenummer: '', gnr: null, bnr: null, fullstendigMatrikkel: 'UAVKLART' }),
      ...(fetchedProperty ? {
        gnr: fetchedProperty.gnr,
        bnr: fetchedProperty.bnr,
        fullstendigMatrikkel: fetchedProperty.fullstendigMatrikkel,
        ownerName: fetchedProperty.ownerName,
      } : {}),
      ...(derivedEierform ? { eierform: derivedEierform } : {}),
      ...(region?.municipality ? { kommunenavn: region.municipality } : {}),
      ...(region?.municipality_number ? { kommunenummer: region.municipality_number } : {}),
      ...(region?.county ? { fylkesnavn: region.county } : {}),
      ...(region?.county_number ? { fylkesnummer: region.county_number } : {})
    };

    return {
      ...spot,
      isInnmark: innmark,
      tags: allTags,
      county: region?.county || spot.county,
      county_slug: region?.county_slug || spot.county_slug,
      municipality: region?.municipality || spot.municipality,
      municipality_slug: region?.municipality_slug || spot.municipality_slug,
      property: nextProperty,
      terrainSlopePercent: terrainSlopePercent ?? spot.terrainSlopePercent
    };
  } catch (err) {
    console.warn('[geoValidation] enrichSpotWithGeoData failed, returning original spot', err);
    return spot;
  }
};

export const isAiUnconfirmedSpot = (spot: CampingSpot): boolean => {
  const isAi = spot.spotType === 'algorithmic';
  const isConfirmed = spot.sourceLevel === 4 || spot.spotType === 'confirmed' || spot.isVerified === true;
  return isAi && !isConfirmed;
};
